/* eslint-disable camelcase */
import qs from 'query-string';
import { ExportFormat } from '../../constants/file';
import { decamelizeKeys } from '../../lib';
import {
    CountResponse,
    PaginatedRequest,
    PaginatedResponse,
} from '../../lib/pagination';
import {
    RentalSplitSnapshot,
    NewRentalSplitSnapshot,
    RentalSplitInput,
    UpdateSplitStatusArgs,
    SplitFilterSummarySnapshot,
    RentalResultWithDestination,
    RentalSplitWorkspaceListingResult,
} from '../../models/RentalPayment';
import api, { rawClient } from '../api';
import { SplitFilterStatus } from '../../constants/rental';
import { DateTime } from 'luxon';
import { getBackVersion } from '../../config';
import { RentalSplitChartsSnapshot } from '../../models/RentalPayment/Split/SplitsCharts';
import { RentalSplitChartsByDestinationSnapshot } from '../../models/RentalPayment/Split/SplitsChartsByDestination';

const getContractSplit = (
    splitId: RentalSplitSnapshot['id'],
    contractId: string
): Promise<RentalResultWithDestination<RentalSplitInput[]>> =>
    api.request<RentalResultWithDestination<RentalSplitInput[]>>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}`,
    });

const getContractSplits = (
    contractId: string
): Promise<RentalResultWithDestination<RentalSplitInput[]>> =>
    api.request<
        RentalSplitWorkspaceListingResult<
            RentalResultWithDestination<RentalSplitInput[]>
        >
    >({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits`,
    });

const downloadContractSplitsReport = (
    contractId: string,
    format: ExportFormat
): Promise<void> =>
    rawClient.download({
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${format}`,
    });

export type SplitDateRequestFilter = {
    minDate?: string;
    maxDate?: string;
};

export type SplitRequestFilter = SplitDateRequestFilter & {
    referenceMonth?: string;
    landlord?: string;
    tenant?: string;
    status?: SplitFilterStatus[];
    shouldFilterByTransfer?: boolean;
};

type RentalSplitPaginatedResponse = {
    splitListing: RentalSplitWorkspaceListingResult<
        RentalResultWithDestination<RentalSplitInput[]>
    >;
} & PaginatedResponse;

const getPaginatedSplits = (
    params: PaginatedRequest & SplitRequestFilter = {}
): Promise<RentalSplitPaginatedResponse> =>
    api.request<RentalSplitPaginatedResponse>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/splits`,
        params: decamelizeKeys({ ...params, paginate: true }),
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });

const downloadSplitsReport = (
    format: ExportFormat,
    filters: SplitRequestFilter
): Promise<void> =>
    rawClient.download({
        url: `${getBackVersion()}/agent/rental/splits/download/${format}`,
        params: decamelizeKeys(filters),
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });

const requestSplitsReport = (
    format: ExportFormat,
    filters: SplitRequestFilter
): Promise<void> =>
    api.request({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/splits/${format}`,
        params: decamelizeKeys(filters),
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });

const getSplit = (
    splitId: RentalSplitSnapshot['id']
): Promise<
    RentalSplitWorkspaceListingResult<
        RentalResultWithDestination<RentalSplitInput[]>
    >
> =>
    api.request<
        RentalSplitWorkspaceListingResult<
            RentalResultWithDestination<RentalSplitInput[]>
        >
    >({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/splits/${splitId}`,
    });

const createSplit = (
    data: NewRentalSplitSnapshot,
    contractId: string
): Promise<RentalSplitInput> =>
    api.request({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits`,
        data: decamelizeKeys(data),
    });

const retrySplit = (
    splitId: string,
    contractId: string,
    splitTransferId: string,
    destinationId: number | null
): Promise<RentalSplitInput> =>
    api.request({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}/retry`,
        data: decamelizeKeys({
            scheduledAt: DateTime.now().toISO(),
            destinationId,
            splitTransferId,
            scheduleType: 'immediate',
        }),
    });

const updateSplit = (
    data: NewRentalSplitSnapshot,
    splitId: string,
    contractId: string
): Promise<RentalSplitInput> =>
    api.request({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}`,
        data: decamelizeKeys(data),
    });

const removeSplit = (
    splitId: RentalSplitSnapshot['id'],
    contractId: string
): Promise<void> =>
    api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}`,
        params: { action: 'remove' },
    });

const archiveSplit = (
    splitId: RentalSplitSnapshot['id'],
    action: 'archive' | 'unarchive',
    contractId: string
): Promise<RentalSplitInput> =>
    api.request<RentalSplitInput>({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}`,
        params: { action: action },
    });

const setSplitStatus = (
    splitId: RentalSplitSnapshot['id'],
    contractId: string,
    data: UpdateSplitStatusArgs
): Promise<RentalSplitInput> =>
    api.request<RentalSplitInput>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}/status`,
        params: decamelizeKeys({
            ...data,
            paidAt: data.paidAt?.toISO(),
            scheduledAt: data.scheduledAt?.toISO(),
        }),
    });

const setSplitTransferNow = (
    splitId: RentalSplitSnapshot['id'],
    contractId: string
): Promise<RentalSplitInput> =>
    api.request<RentalSplitInput>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}/transfer`,
    });

const downloadSplitReceipt = (
    splitId: RentalSplitSnapshot['id'],
    contractId: string,
    destinationId: number,
    transferId?: string
): Promise<void> =>
    rawClient.download({
        method: 'GET',
        params: { transfer_id: transferId },
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}/receipt/${destinationId}`,
    });

const downloadSplitEmptyReceipt = (
    splitId: RentalSplitSnapshot['id'],
    contractId: string
): Promise<void> =>
    rawClient.download({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/splits/${splitId}/receipt/empty`,
    });

const countDoneSplits = (): Promise<CountResponse> =>
    api.request({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/splits/done/count`,
    });

const getSplitsSummary = (
    filters: SplitDateRequestFilter
): Promise<SplitFilterSummarySnapshot> => {
    return api.request({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/splits/summary`,
        params: decamelizeKeys(filters),
    });
};

const getSplitsChartsDataPoints = (
    minDate?: DateTime,
    maxDate?: DateTime
): Promise<RentalSplitChartsSnapshot[]> => {
    return api.request<RentalSplitChartsSnapshot[]>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/splits/charts`,
        params: {
            max_date: maxDate?.toISODate(),
            min_date: minDate?.toISODate(),
        },
    });
};

const getSplitsChartsByDestinationDataPoints = (
    minDate?: DateTime,
    maxDate?: DateTime
): Promise<RentalSplitChartsByDestinationSnapshot[]> => {
    return api.request<RentalSplitChartsByDestinationSnapshot[]>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/splits/charts/destination`,
        params: {
            max_date: maxDate?.toISODate(),
            min_date: minDate?.toISODate(),
        },
    });
};

export {
    archiveSplit,
    createSplit,
    downloadSplitReceipt,
    downloadSplitEmptyReceipt,
    getContractSplit,
    getContractSplits,
    getSplit,
    getSplitsChartsByDestinationDataPoints,
    getSplitsChartsDataPoints,
    getPaginatedSplits,
    removeSplit,
    retrySplit,
    setSplitStatus,
    setSplitTransferNow,
    updateSplit,
    downloadSplitsReport,
    downloadContractSplitsReport,
    countDoneSplits,
    getSplitsSummary,
    requestSplitsReport,
};

export type { RentalSplitPaginatedResponse };
